import { getSpansForElement } from '../svgTextDisplay/getSpansForElement';
import type { TextElementToDisplay } from './types';
import { getHighlightLinePadding, getTextContentWidth } from './textBoxMetrics';
import { BACKGROUND_TEXT_MODE } from '../../../constants';
import { getTextLineGeometry } from './textLineGeometry';
import { selectFontMetrics } from './selectFontMetrics';
import { hasMixedTypographyMarkers } from './mixedTypography';

type VerticalTrimFontMetrics = {
  marginOffsetTop: number;
  height: number;
  heightWithoutVerticalTrim: number;
};

type StandardFontMetrics = {
  marginOffsetTop: number;
  marginOffsetBottom: number;
  standardTrimHeight?: number;
};

type FontMetrics = VerticalTrimFontMetrics | StandardFontMetrics;

export function calcFontMetrics({
  element,
  dataToEditorX,
  dataToEditorY,
  calculateStandardTrimHeight = false,
}: {
  element: TextElementToDisplay;
  dataToEditorX?: (value: number) => number;
  dataToEditorY?: (value: number) => number;
  calculateStandardTrimHeight?: boolean;
}): FontMetrics {
  if (element.verticalTrim === 'baseline' && dataToEditorX && dataToEditorY) {
    return calcVerticalTrimFontMetrics({
      element,
      dataToEditorX,
      dataToEditorY,
    });
  }

  const hasMixedTypography = hasMixedTypographyMarkers(element.content);
  const selectedMetrics = selectFontMetrics(element.font);

  if (
    (hasMixedTypography || (selectedMetrics && calculateStandardTrimHeight)) &&
    dataToEditorX &&
    dataToEditorY
  ) {
    const spans = getSpansForElement({
      dataToEditorX,
      dataToEditorY,
      text: element,
      width: getTextContentWidth({
        element,
        dataToEditorX,
        width: dataToEditorX(element.width),
      }),
    });
    const lines = getTextLineGeometry({
      spans: spans.map((span) => ({
        lineNumber: span.lineNumber,
        font: span.font,
        fontSize: span.fontSizeInDataUnits,
        fontSizeOverride: span.fontSizeOverride,
        lineHeight: span.lineHeight,
        lineHeightOverride: span.lineHeightOverride,
      })),
      defaultFont: element.font,
      defaultFontSize: element.fontSize,
      lineHeight: element.lineHeight,
      paddingBetweenLines:
        element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
          ? element.padding?.vertical || 0
          : 0,
    });
    const firstLine = lines[0];
    const lastLine = lines[lines.length - 1];
    if (firstLine && lastLine) {
      const editorYScale = dataToEditorY(1);
      const editorToDataY = (value: number) =>
        editorYScale === 0 ? value : value / editorYScale;
      const listSpacing = editorToDataY(
        Math.max(0, ...spans.map((span) => span.lineOffsetY))
      );
      const verticalPadding = element.padding?.vertical || 0;

      return {
        marginOffsetTop: firstLine.ascent - firstLine.glyphAscent,
        marginOffsetBottom: lastLine.descent - lastLine.glyphDescent,
        standardTrimHeight:
          verticalPadding * 2 +
          lastLine.baseline -
          firstLine.baseline +
          listSpacing +
          firstLine.glyphAscent +
          lastLine.glyphDescent,
      };
    }
  }

  if (!selectedMetrics) {
    return {
      marginOffsetTop: 0,
      marginOffsetBottom: 0,
    };
  }

  const { fontSize, lineHeight } = element;
  const { upm, asc, des } = selectedMetrics;

  // We cant to cut some of the "virtual-area"
  // More info: https://iamvdo.me/en/blog/css-font-metrics-line-height-and-vertical-align
  const contentAreaPx = ((asc - des) / upm) * fontSize;
  const lineBoxPx = lineHeight * fontSize;
  const marginOffset = lineBoxPx - contentAreaPx;

  return {
    marginOffsetTop: marginOffset / 2,
    marginOffsetBottom: marginOffset / 2,
  };
}

/**
 * Computes Figma-like text box heights for:
 * - Standard (blue): top = baseline - ascent, bottom = baseline + descent
 * - Cap height to baseline (red): top = baseline - capHeight, bottom = baseline
 *
 * Assumptions:
 * - The boxes do NOT depend on which glyphs are present (matches your observation).
 * - Line height in px is rounded to the nearest integer (matches your 294px at 3 lines with 84px * 1.17em).
 */
export function calcVerticalTrimFontMetrics({
  element,
  dataToEditorX,
  dataToEditorY,
}: {
  element: TextElementToDisplay;
  dataToEditorX: (value: number) => number;
  dataToEditorY: (value: number) => number;
}): VerticalTrimFontMetrics {
  const selectedMetrics = selectFontMetrics(element.font);
  const hasMixedTypography = hasMixedTypographyMarkers(element.content);
  if (!selectedMetrics && !hasMixedTypography) {
    return {
      marginOffsetTop: 0,
      height: 0,
      heightWithoutVerticalTrim: 0,
    };
  }
  const fullWidthPx = dataToEditorX(element.width);
  const contentWidthPx = getTextContentWidth({
    element,
    dataToEditorX,
    width: fullWidthPx,
  });
  const editorYScale = dataToEditorY(1);
  const editorToDataY = (value: number) =>
    editorYScale === 0 ? value : value / editorYScale;
  const verticalPaddingPx = element.padding?.vertical || 0;
  const highlightLinePaddingPx = getHighlightLinePadding({
    element,
    dataToEditorX,
  });
  const highlightLinePaddingDataPx = editorToDataY(highlightLinePaddingPx);
  const mixedHighlightLinePadding =
    element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
      ? element.padding?.vertical || 0
      : 0;

  const spans = getSpansForElement({
    dataToEditorX,
    dataToEditorY,
    text: element,
    width: contentWidthPx,
  });

  const lineCount =
    spans.length === 0
      ? 0
      : Math.max(...spans.map((span) => span.lineNumber)) + 1;
  const listSpacingPx =
    spans.length === 0
      ? 0
      : editorToDataY(Math.max(...spans.map((span) => span.lineOffsetY)));
  const { fontSize: fontSizePx, lineHeight: lineHeightEm } = element;

  const effectiveMetrics = selectedMetrics || {
    upm: 1,
    asc: 0.8,
    des: -0.2,
    capHeight: 0.56,
  };

  const lineHeightPx = fontSizePx * lineHeightEm;
  const lineSpacingPx = lineHeightPx + highlightLinePaddingDataPx * 2;

  const ascentPx = (fontSizePx * effectiveMetrics.asc) / effectiveMetrics.upm;
  const descentPx =
    (fontSizePx * Math.abs(effectiveMetrics.des)) / effectiveMetrics.upm;
  const capHeightPx =
    (fontSizePx * effectiveMetrics.capHeight) / effectiveMetrics.upm;

  const fontBoxPx = ascentPx + descentPx;
  const leadingPx = lineHeightPx - fontBoxPx;
  const baselineFromTopPx = leadingPx / 2 + ascentPx;
  const topGapPx = baselineFromTopPx - capHeightPx;

  if (hasMixedTypography) {
    const lines = getTextLineGeometry({
      spans: spans.map((span) => ({
        lineNumber: span.lineNumber,
        font: span.font,
        fontSize: span.fontSizeInDataUnits,
        fontSizeOverride: span.fontSizeOverride,
        lineHeight: span.lineHeight,
        lineHeightOverride: span.lineHeightOverride,
      })),
      defaultFont: element.font,
      defaultFontSize: element.fontSize,
      lineHeight: element.lineHeight,
      paddingBetweenLines: mixedHighlightLinePadding,
    });
    const firstLine = lines[0];
    const lastLine = lines[lines.length - 1];
    if (!firstLine || !lastLine) {
      return {
        marginOffsetTop: 0,
        height: 0,
        heightWithoutVerticalTrim: 0,
      };
    }
    const mixedTopGap = firstLine.baseline - firstLine.capHeight;
    const baselineDistance =
      lastLine.baseline - firstLine.baseline + listSpacingPx;
    const mixedBaselineY = verticalPaddingPx + lastLine.baseline - mixedTopGap;
    const mixedBottomPadding =
      element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
        ? Math.max(verticalPaddingPx, lastLine.glyphDescent)
        : verticalPaddingPx;
    const standardMixedHeight =
      verticalPaddingPx * 2 +
      baselineDistance +
      firstLine.glyphAscent +
      lastLine.glyphDescent;
    const trimmedMixedHeight = Math.ceil(mixedBaselineY + mixedBottomPadding);
    const mixedGlyphHeight =
      verticalPaddingPx * 2 + baselineDistance + firstLine.capHeight;
    const hasTextBox =
      element.backgroundTextMode &&
      element.backgroundTextMode !== BACKGROUND_TEXT_MODE.NONE;
    const mixedHeight =
      element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
        ? Math.ceil(standardMixedHeight)
        : hasTextBox
        ? Math.ceil(mixedGlyphHeight)
        : trimmedMixedHeight;

    return {
      marginOffsetTop: Math.floor(
        element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
          ? firstLine.ascent - firstLine.glyphAscent - verticalPaddingPx
          : mixedTopGap
      ),
      height: mixedHeight,
      heightWithoutVerticalTrim: Math.max(0, Math.ceil(standardMixedHeight)),
    };
  }

  const lastLineTopPx =
    Math.max(0, lineCount - 1) * lineSpacingPx + listSpacingPx - topGapPx;

  const baselineY = verticalPaddingPx + lastLineTopPx + baselineFromTopPx;
  const bottomPaddingPx =
    element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
      ? Math.max(verticalPaddingPx, descentPx)
      : verticalPaddingPx;

  // Standard: (first baseline up to top) + (baseline gaps) + (last baseline down to bottom)
  const standardBoxHeightPx =
    lineCount === 0
      ? 0
      : verticalPaddingPx * 2 +
        ascentPx +
        (lineCount - 1) * lineSpacingPx +
        listSpacingPx +
        descentPx;
  const trimmedBoxHeightPx = Math.ceil(baselineY + bottomPaddingPx);
  const textBoxGlyphHeightPx =
    lineCount === 0
      ? 0
      : verticalPaddingPx * 2 +
        (lineCount - 1) * lineSpacingPx +
        listSpacingPx +
        capHeightPx;
  const highlightTextBoxHeightPx =
    lineCount === 0
      ? 0
      : verticalPaddingPx * 2 +
        (lineCount - 1) * lineSpacingPx +
        listSpacingPx +
        fontBoxPx;
  const hasTextBox =
    element.backgroundTextMode &&
    element.backgroundTextMode !== BACKGROUND_TEXT_MODE.NONE;
  const textBoxHeightPx =
    element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
      ? Math.ceil(highlightTextBoxHeightPx)
      : hasTextBox
      ? Math.ceil(textBoxGlyphHeightPx)
      : trimmedBoxHeightPx;
  const marginOffsetTopPx =
    element.backgroundTextMode === BACKGROUND_TEXT_MODE.HIGHLIGHT
      ? leadingPx / 2 - verticalPaddingPx
      : topGapPx;

  return {
    marginOffsetTop: Math.floor(marginOffsetTopPx),
    height: textBoxHeightPx,
    // If the content is empty (lineCount = 0), the height should be 0
    heightWithoutVerticalTrim: Math.max(0, Math.ceil(standardBoxHeightPx)),
  };
}
